import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "services/supabaseClient";
import SectionCard from "components/SectionCard";
import FooterCompact from "components/FooterCompact";
import {
  clearSessionLogoutFlags,
  writeLastActivity,
} from "lib/sessionActivity";

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();
const normalizeCode = (value) => String(value || "").replace(/\s+/g, "");

function initialEmail() {
  try { return normalizeEmail(new URL(window.location.href).searchParams.get("email")); } catch { return ""; }
}

function initialRecoveryMode() {
  try {
    return new URL(window.location.href).searchParams.get("flow") === "recovery";
  } catch {
    return false;
  }
}

export default function Activate() {
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [recoveryMode, setRecoveryMode] = useState(initialRecoveryMode);
  const navigate = useNavigate();

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = window.setTimeout(
      () => setResendCooldown((seconds) => Math.max(0, seconds - 1)),
      1000
    );
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    const normalizedEmail = normalizeEmail(email);
    const normalizedCode = normalizeCode(code);
    if (!normalizedEmail || !normalizedCode) {
      setError("Enter your email address and verification code.");
      return;
    }
    setLoading(true);
    try {
      // verifyOtp emits SIGNED_IN before this promise resolves. Establish a
      // fresh inactivity baseline first so an old browser timestamp cannot
      // immediately expire the newly verified activation session.
      clearSessionLogoutFlags();
      writeLastActivity(Date.now());
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email: normalizedEmail,
        token: normalizedCode,
        type: recoveryMode ? "recovery" : "invite",
      });
      if (verifyError || !data?.user || !data?.session) throw verifyError || new Error("missing_session");
      navigate("/set-password", { replace: true });
    } catch {
      setError("The verification code is invalid or has expired. Please request a new code.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    const normalizedEmail = normalizeEmail(email);
    setError("");
    setResendSent(false);

    if (!normalizedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setError("Enter a valid email address before requesting another code.");
      return;
    }

    setResending(true);
    try {
      const { error: resendError } = await supabase.auth.resetPasswordForEmail(
        normalizedEmail,
        {
          redirectTo: `${window.location.origin}/activate?flow=recovery&email=${encodeURIComponent(
            normalizedEmail
          )}`,
        }
      );
      if (resendError) throw resendError;

      // Keep this response generic so the page does not reveal whether an
      // account or pending invitation exists for the supplied address.
      setResendSent(true);
      setResendCooldown(60);
      setRecoveryMode(true);
      setCode("");
      navigate(
        `/activate?flow=recovery&email=${encodeURIComponent(normalizedEmail)}`,
        { replace: true }
      );
    } catch {
      setError("Another code could not be requested right now. Please wait and try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-app text-app flex flex-col">
      <div className="flex-1 flex items-center justify-center p-6 bg-surface">
        <div className="w-full max-w-md">
          <h1 className="text-2xl font-semibold">Activate Account</h1>
          <SectionCard className="mt-4">
            <p className="mb-4 text-sm text-muted">
              {recoveryMode
                ? "Enter the new verification code that was sent to your email address."
                : "Enter the email address that received the invitation and the verification code shown in the email."}
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><label className="block mb-1 font-medium">Email address</label><input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-2 rounded-md border border-app input-app" disabled={loading} required /></div>
              <div><label className="block mb-1 font-medium">Verification code</label><input type="text" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} className="w-full px-4 py-2 rounded-md border border-app input-app" disabled={loading} required /></div>
              <button className="btn btn-primary w-full" disabled={loading || resending}>
                {loading ? "Verifying..." : "Activate Account"}
              </button>
            </form>
            {error && <div role="alert" className="mt-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
            <div className="mt-4 space-y-3 text-center text-sm">
              <button
                type="button"
                className="linkCls"
                onClick={handleResend}
                disabled={loading || resending || resendCooldown > 0}
              >
                {resending
                  ? "Requesting another code..."
                  : resendCooldown > 0
                    ? `Request another code (${resendCooldown}s)`
                    : "Request another code"}
              </button>

              {resendSent && (
                <div className="rounded-md border border-green-500/40 bg-green-500/10 p-3 text-left text-sm text-app">
                  <p>
                    If an activation is available for this email address, a new
                    verification code has been sent. Enter the new code above
                    to continue activating your account.
                  </p>
                </div>
              )}
            </div>
          </SectionCard>
        </div>
      </div>
      <FooterCompact />
    </div>
  );
}
